import React, {useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {ProgressSpinner} from "primereact/progressspinner";
import {Tag} from "primereact/tag";
import Avatar from "../../../system/components/avatar";
import useUserEventLog from "../../../models/user-event-log/use-user-event-log";
import {workspaceGraphSelectors} from "../../../system/workspace-graph-slice";
import useEntitySidebars from "../../../system/entity-sidebars/use-entity-sidebars";
import {useTranslate} from "../../../system/useTranslate";
import {
    entityKey,
    formatEventDate,
    getCategoryMeta,
    getDateBucket,
    includesSearch,
    isActivityItem,
} from "../inbox-utils";

const filters = [
    {label: "همه", value: "all", icon: "pi pi-th-large"},
    {label: "پروژه‌ها", value: "project", icon: "pi pi-folder"},
    {label: "اهداف", value: "goal", icon: "pi pi-flag"},
    {label: "وظایف", value: "task", icon: "pi pi-check-square"},
    {label: "تیم", value: "team", icon: "pi pi-users"},
    {label: "دسترسی‌ها", value: "access", icon: "pi pi-lock"},
    {label: "گزارش‌ها", value: "report", icon: "pi pi-chart-bar"},
];

const bucketOrder = ["امروز", "دیروز", "قدیمی‌تر"];

function referenceFromActivityItem(item) {
    const rawType = item?.sourceType || item?.payload?.sourceType;
    const type = rawType === "milestone" ? "deliverable" : rawType;
    const key = item?.sourceDocumentId
        || item?.payload?.sourceDocumentId
        || item?.payload?.documentId
        || item?.payload?.sourceId;
    if (!type || !key) return null;
    return {
        ...item?.payload,
        id: key,
        documentId: key,
        type,
        title: item?.sourceLabel || item?.title,
        link: item?.actionUrl || item?.payload?.actionUrl || item?.payload?.link,
    };
}

function openActionUrl(actionUrl, navigate) {
    if (!actionUrl) return false;
    const target = String(actionUrl).replace(/^#/, "");
    if (!target || /^https?:\/\//i.test(target) || /^\/\//.test(target)) return false;
    navigate(target);
    return true;
}

function navigateToReferencePage(reference, navigate) {
    const key = entityKey(reference);
    if (!key) return false;
    const routes = {
        goal: `/goals/${key}`,
        project: `/projects/${key}`,
        task: `/tasks?task=${key}`,
        team: `/teams/${key}`,
        customer: `/customers/${key}`,
        "report-dashboard": `/reports/${key}`,
        report: `/reports/${key}`,
    };
    if (!routes[reference?.type]) return false;
    navigate(routes[reference.type]);
    return true;
}

function ActivityTab() {
    const translate = useTranslate();
    const inboxItems = useSelector(workspaceGraphSelectors.inboxItems);
    const navigate = useNavigate();
    const eventApi = useUserEventLog();
    const [activeFilter, setActiveFilter] = useState("all");
    const [search, setSearch] = useState("");
    const entitySidebars = useEntitySidebars();

    const groupedItems = useMemo(() => {
        const visible = (inboxItems || [])
            .filter(isActivityItem)
            .filter((item) => activeFilter === "all" || item.eventCategory === activeFilter)
            .filter((item) => includesSearch(item, search))
            .sort((left, right) => new Date(right?.createdAt || 0) - new Date(left?.createdAt || 0));

        return bucketOrder.map((bucket) => ({
            bucket,
            items: visible.filter((item) => getDateBucket(item.createdAt) === bucket),
        })).filter((group) => group.items.length);
    }, [activeFilter, inboxItems, search]);

    const markRead = async (item) => {
        if (item.isRead) return;
        await eventApi.updateUserEventLog(entityKey(item), {isRead: true});
    };

    const handleOpenReference = async (event, item) => {
        event.preventDefault();
        event.stopPropagation();
        await markRead(item);
        const reference = referenceFromActivityItem(item);
        if (reference && await entitySidebars.open(reference)) return;
        if (openActionUrl(reference?.link || item.actionUrl, navigate)) return;
        if (reference) navigateToReferencePage(reference, navigate);
    };

    return (
        <div className="h-full min-h-0 overflow-auto p-3 md:p-4">
            <div className="surface-card border-round-xl border-1 border-200 overflow-hidden mx-auto" style={{maxWidth: "70rem"}}>
                <div className="flex flex-wrap align-items-center justify-content-between gap-3 p-3 border-bottom-1 border-200">
                    <div className="flex flex-wrap gap-2">
                        {filters.map((filter) => (
                            <Button
                                key={filter.value}
                                label={filter.label}
                                icon={filter.icon}
                                size="small"
                                outlined={activeFilter !== filter.value}
                                text={activeFilter !== filter.value}
                                className="text-xs gap-1"
                                onClick={() => setActiveFilter(filter.value)}
                            />
                        ))}
                    </div>
                    <Button
                        label="همه را خوانده‌شده کن"
                        icon="pi pi-check-circle"
                        size="small"
                        text
                        className="text-xs gap-1"
                        loading={eventApi.loading}
                        onClick={() => eventApi.markAllRead()}
                    />
                    <IconField className="align-items-center flex w-full md:w-18rem">
                        <InputIcon className="pi pi-search"/>
                        <InputText
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="جست‌وجو در فعالیت‌ها..."
                            className="w-full border-round-lg pt-2 pb-2 text-xs"
                        />
                    </IconField>
                </div>

                {!groupedItems.length ? (
                    <div className="flex flex-column align-items-center justify-content-center text-center text-500 p-6" style={{minHeight: "22rem"}}>
                        <i className="pi pi-bolt text-4xl text-300 mb-3"/>
                        <strong className="text-700 text-xs">فعالیتی برای نمایش نیست</strong>
                        <small className="mt-2 text-xs">رویدادهای پروژه، هدف، وظیفه و تیم اینجا نمایش داده می‌شوند.</small>
                    </div>
                ) : groupedItems.map((group) => (
                    <section key={group.bucket} className="p-3 md:p-4 border-bottom-1 border-100 last:border-none">
                        <h2 className="text-sm font-bold text-700 mt-0 mb-3">{group.bucket}</h2>
                        <div className="flex flex-column">
                            {group.items.map((item, index) => {
                                const meta = getCategoryMeta(item.eventCategory);
                                return (
                                    <div key={entityKey(item)} className="flex gap-3 cursor-pointer" onClick={() => markRead(item)}>
                                        <div className="flex flex-column align-items-center">
                                            <div className={`w-3rem h-3rem border-circle flex align-items-center justify-content-center bg-${meta.color}-50 text-${meta.color}-600 border-1 border-${meta.color}-100`}>
                                                {item.actor?.member ? <Avatar memberData={item.actor.member}/> : <i className={meta.icon}/>}
                                            </div>
                                            {index < group.items.length - 1 && <div className="flex-1 border-left-1 border-200 my-1"/>}
                                        </div>
                                        <article className={`flex-1 min-w-0 pb-4 ${!item.isRead ? "font-medium" : ""}`}>
                                            <div className="flex flex-wrap align-items-center gap-2 mb-1">
                                                {!item.isRead && <span className="w-1rem h-1rem border-circle bg-primary" style={{width: ".5rem", height: ".5rem"}}/>}
                                                <span className="text-xs text-900">{translate(item.title || item.eventKind || meta.label)}</span>
                                                <Tag value={meta.label} severity="secondary" rounded/>
                                                <time className="text-xs text-500 mr-auto">{formatEventDate(item.createdAt)}</time>
                                            </div>
                                            {item.message && <p className="text-xs text-600 line-height-3 m-0 mb-2">{translate(item.message)}</p>}
                                            {(item.sourceLabel || item.contextLabel) && (
                                                <Button
                                                    type="button"
                                                    text
                                                    size="small"
                                                    icon={meta.icon}
                                                    label={[item.sourceLabel, item.contextLabel].filter(Boolean).join(" / ")}
                                                    className="surface-50 border-1 border-100 border-round px-2 py-1 text-xs text-600 shadow-none gap-2 hover:surface-100"
                                                    onClick={(event) => handleOpenReference(event, item)}
                                                />
                                            )}
                                        </article>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ))}
                {eventApi.loading && (
                    <div className="flex justify-content-center p-2"><ProgressSpinner style={{width: "1.5rem", height: "1.5rem"}} strokeWidth="4"/></div>
                )}
            </div>
            {entitySidebars.sidebars}
        </div>
    );
}

export default ActivityTab;
